import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import BlueBulbIcon from '../../assets/icons/filled-bulb-icon.svg?react';
import HammerIcon from '../../assets/icons/hammer-active-icon.svg?react';
import CheckIcon from '../../assets/icons/check-green-icon.svg?react';
import CalenderIcon from '../../assets/icons/calender-icon.svg?react';
import RetryIcon from '../../assets/icons/retry-icon.svg?react';
import TimeDurationIcon from '../../assets/icons/time-duration-icon.svg?react';
import ThreeQuarterIcon from '../../assets/icons/three-quarter.svg?react';
import CountUp from 'react-countup';
import Button from '../../components/Button';
import Chart from 'react-apexcharts';
import InjuriesIcon from '../../assets/icons/injuries-icon.svg?react';
import InfoIcon from '../../assets/icons/warning.svg?react';
import {
  affectedBodyPartOptions,
  classificationIllnessOptions,
  correctiveActionByDepartmentOptions,
  correctiveActionsOverviewOptions,
  incidentsBySeverityOptions,
  incidentsByTypeOptions,
  totalIncidentsReportedByDepartmentOptions,
  totalIncidentsReportedOptions,
  typeOfLossOptions,
} from './chartsOptions';
import moment from 'moment';
import Select from '../../components/Select';
import { Link, useNavigate } from 'react-router';
import Loader from '../../components/Loader';
import {
  CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum,
  CorrectiveActionsWithOverdueDaysPriorityEnum,
  GetStatisticResponseChartsDto,
  GetStatisticResponseDto,
  LoginResponseData,
  StatisticsApiStatisticsControllerGetStatisticsInsightRequest,
  StatisticsControllerGetStatisticsInsightCorrectiveActionOverviewEnum,
  StatisticsControllerGetStatisticsInsightIncidentTypeActiveEnum,
  StatisticsControllerGetStatisticsInsightSeverityActiveEnum,
} from '@EHSNavigator/backend';
import { useLocalStorage } from '@uidotdev/usehooks';

interface StatisticsProps {
  otherClasses?: string;
  data: GetStatisticResponseChartsDto;
  params: StatisticsApiStatisticsControllerGetStatisticsInsightRequest;
  isLoading: boolean;
  stats: GetStatisticResponseDto;
}

const Statistics: React.FC<StatisticsProps> = ({
  otherClasses,
  data,
  params,
  isLoading,
  stats,
}) => {
  const statisticsClasses = clsx(otherClasses, 'min-h-screen');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const [selectedTabs, setSelectedTabs] = useState({
    chartCurrentYear: params?.year?.toString() || moment().format('YYYY'),
    incidentByType: params?.incidentTypeActive || 'year',
    incidentsByTypeMonth:
      params?.incidentTypeMonth?.toString() || moment().format('M'),
    correctiveActionsOverview: params?.correctiveActionOverview || 'No-Filter',
    incidentsBySeverity: params?.severityActive || 'year',
    incidentsBySeverityMonth:
      params?.incidentSeverityMonth?.toString() || moment().format('M'),
  });

  const paramsSearch = new URLSearchParams(window.location.search);

  const totalIncidentsReported = totalIncidentsReportedOptions(
    data?.detailsInsightsTrends?.totalIncidentReportedChart,
  );
  const typeOfLoss = typeOfLossOptions(
    data?.detailsInsightsTrends?.typeofLossChart,
  );
  const totalIncidentsReportedByDepartment =
    totalIncidentsReportedByDepartmentOptions(
      data?.detailsInsightsTrends?.totalIncidentReportedByDepartmentChart,
    );

  const totalInjuriesReportedByDepartment =
    totalIncidentsReportedByDepartmentOptions(
      data?.detailsInsightsTrends?.totalInjuriesByDepartmentChart || [],
    );
  const incidentsByType = incidentsByTypeOptions(
    data?.detailsInsightsTrends?.incidentTypeChart,
  );
  const correctiveActionsOverview = correctiveActionsOverviewOptions(
    data?.detailsInsightsTrends?.correctiveActionOverviewChart,
  );
  const correctiveActionsOverviewByDepartment =
    correctiveActionByDepartmentOptions(
      data?.detailsInsightsTrends?.correctiveActionOverviewChart,
    );
  const incidentsBySeverityOverview = incidentsBySeverityOptions(
    data?.detailsInsightsTrends?.incidentSeverityChart,
  );
  const affectedBodyPartOverview = affectedBodyPartOptions(
    data?.detailsInsightsTrends?.affectedBodyPartChart,
  );
  const classificationIllnessOverview = classificationIllnessOptions(
    data?.detailsInsightsTrends?.classificationIllnessChart,
  );

  const overview = stats?.overview;
  const frequencyBreakdown = stats?.frequencyBreakdown;

  useEffect(() => {
    if (selectedTabs?.chartCurrentYear === moment().format('YYYY')) {
      paramsSearch.delete('year');
    } else {
      paramsSearch.set('year', selectedTabs?.chartCurrentYear);
    }
    if (selectedTabs?.incidentsBySeverity === 'year') {
      paramsSearch.delete('severityActive');
      paramsSearch.delete('incidentSeverityMonth');
      setSelectedTabs((prev) => ({
        ...prev,
        incidentsBySeverityMonth: moment().format('M'),
      }));
    } else {
      paramsSearch.set('severityActive', selectedTabs?.incidentsBySeverity);
    }
    if (selectedTabs?.incidentsBySeverityMonth === moment().format('M')) {
      paramsSearch.delete('incidentSeverityMonth');
    } else {
      paramsSearch.set(
        'incidentSeverityMonth',
        selectedTabs?.incidentsBySeverityMonth,
      );
    }
    if (selectedTabs?.incidentByType === 'year') {
      paramsSearch.delete('incidentTypeActive');
      paramsSearch.delete('incidentTypeMonth');
      setSelectedTabs((prev) => ({
        ...prev,
        incidentsByTypeMonth: moment().format('M'),
      }));
    } else {
      paramsSearch.set('incidentTypeActive', selectedTabs?.incidentByType);
    }
    if (selectedTabs?.incidentsByTypeMonth === moment().format('M')) {
      paramsSearch.delete('incidentTypeMonth');
    } else {
      paramsSearch.set('incidentTypeMonth', selectedTabs?.incidentsByTypeMonth);
    }
    if (selectedTabs?.correctiveActionsOverview === 'No-Filter') {
      paramsSearch.delete('correctiveActionOverview');
    } else {
      paramsSearch.set(
        'correctiveActionOverview',
        selectedTabs?.correctiveActionsOverview,
      );
    }
    navigate(`/statistics?${paramsSearch.toString()}`);
  }, [
    selectedTabs?.chartCurrentYear,
    selectedTabs?.incidentsBySeverity,
    selectedTabs?.incidentsBySeverityMonth,
    selectedTabs?.incidentByType,
    selectedTabs?.incidentsByTypeMonth,
    selectedTabs?.correctiveActionsOverview,
  ]);

  const startYear = 2025;
  const currentYear = moment().year();

  const list: { label: string; value: string }[] = [];

  for (let year = startYear; year <= currentYear; year++) {
    list.push({
      label: year.toString(),
      value: year.toString(),
    });
  }

  return (
    <div className={statisticsClasses} data-testid="statistics">
      {isLoading && (
        <Loader
          variant="compact"
          otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
        />
      )}
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Statistics Dashboard</h1>
        </div>
      </div>

      <div className="mt-8 w-full lg:mt-11">
        <h5 className="text-neutral-tones-text font-inter-variable text-lg font-bold lg:text-xl">
          Overview
        </h5>
        <div className="mt-15 grid grid-cols-2 gap-15 md:grid-cols-4 md:gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-10 lg:w-10" />
              <h6
                className={clsx(
                  'h1 text-brand-byzantine leading-6 lg:leading-11',
                )}
              >
                {overview?.totalIncidents && (
                  <CountUp
                    start={0}
                    end={overview?.totalIncidents}
                    duration={5}
                  />
                )}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-185 text-base leading-5 font-medium lg:text-lg">
              Total Incidents Reported
            </p>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <HammerIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-10 lg:w-10" />
              <h6
                className={clsx(
                  'h1 text-brand-byzantine leading-6 lg:leading-11',
                )}
              >
                {overview?.totalCorrectiveActions && (
                  <CountUp
                    start={0}
                    end={overview?.totalCorrectiveActions}
                    duration={5}
                  />
                )}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-185 text-base leading-5 font-medium lg:text-lg">
              Active Corrective Actions
            </p>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <h6
                className={clsx(
                  'font-ulagadi-sans text-brand-byzantine line-clamp-1 text-2xl leading-8 font-bold lg:text-3xl lg:leading-11 2xl:text-4xl',
                )}
              >
                {overview?.mostReportedDepartment}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-185 text-base leading-5 font-medium lg:text-lg">
              Most Reported Department
            </p>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <CheckIcon className="[&>path]:fill-brand-papery h-6 w-6 lg:h-10 lg:w-10" />

              <h6
                className={clsx('text-brand-papery h1 leading-6 lg:leading-11')}
              >
                {overview?.totalIncidentsResolved && (
                  <CountUp
                    start={0}
                    end={overview?.totalIncidentsResolved}
                    duration={5}
                  />
                )}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-185 text-base leading-5 font-medium lg:text-lg">
              All Time Incidents Resolved
            </p>
          </div>
        </div>
      </div>
      <div className="mt-8 w-full lg:mt-11">
        <h5 className="text-neutral-tones-text font-inter-variable text-lg font-bold lg:text-xl">
          Incident Frequency Breakdowns
        </h5>
        <div className="mt-15 grid grid-cols-2 gap-15 md:grid-cols-4 md:gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <RetryIcon className="[&>path]:fill-brand-fair-orange h-6 w-6 lg:h-8 lg:w-8" />

              <h6
                className={clsx(
                  'text-brand-fair-orange font-ulagadi-sans line-clamp-1 text-2xl leading-8 font-bold lg:text-3xl lg:leading-11 2xl:text-4xl',
                )}
              >
                {frequencyBreakdown?.timeSinceLastIncidentReported}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-174 text-base leading-7 font-medium lg:text-lg">
              Time Since Last Incident
            </p>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <TimeDurationIcon className="[&>path]:fill-brand-fair-orange h-6 w-6 lg:h-10 lg:w-10" />

              <h6
                className={clsx(
                  'text-brand-fair-orange font-ulagadi-sans line-clamp-1 text-2xl leading-8 font-bold lg:text-3xl lg:leading-11 2xl:text-4xl',
                )}
              >
                {frequencyBreakdown?.avgTimeBetweenReportedIncident}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 max-w-174 text-base leading-5 font-medium lg:text-lg">
              Avg. Time b/w Reports
            </p>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 col-span-2 border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Incidents Reported vs. Resolved
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is the ratio of incidents reported vs. resolved for the last
              three months.
            </p>
            <div className="mt-2.5 flex flex-wrap items-start justify-between gap-6 sm:justify-start">
              <div className="flex flex-col">
                <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold">
                  This Month
                </p>
                <div className="flex items-center gap-1">
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-byzantine text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved?.thisMonth
                      ?.total && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.thisMonth?.total
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-neutral-tones-text/50 text-4xl font-bold">
                    :
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-papery text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved?.thisMonth
                      ?.resolved && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.thisMonth?.resolved
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                </div>
              </div>
              <div className="flex flex-col">
                <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold">
                  Last Month
                </p>
                <div className="flex items-center gap-1">
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-byzantine text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved?.lastMonth
                      ?.total && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.lastMonth?.total
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-neutral-tones-text/50 text-4xl font-bold">
                    :
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-papery text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved?.lastMonth
                      ?.resolved && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.lastMonth?.resolved
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                </div>
              </div>
              <div className="flex flex-col">
                <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold">
                  {moment().subtract(2, 'months').format('MMMM YYYY')}
                </p>
                <div className="flex items-center gap-1">
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-byzantine text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved
                      ?.secondLastMonth?.total && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.secondLastMonth?.total
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-neutral-tones-text/50 text-4xl font-bold">
                    :
                  </h6>
                  <h6 className="The font-ulagadi-sans lg:text-h2 text-brand-papery text-4xl font-bold">
                    {frequencyBreakdown?.incidentReportedResolved
                      ?.secondLastMonth?.resolved && (
                      <CountUp
                        start={0}
                        end={
                          frequencyBreakdown?.incidentReportedResolved
                            ?.secondLastMonth?.resolved
                        }
                        duration={5}
                      />
                    )}
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="relative z-0 mt-8 w-full lg:mt-11">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h5 className="text-neutral-tones-text font-inter-variable text-lg font-bold lg:text-xl">
            Detailed Insights & Trends
          </h5>
          <div className="flex items-center gap-2.5">
            <p className="text-neutral-tones-text font-inter-variable flex items-center text-lg font-medium italic">
              <CalenderIcon className="[&>g>path]:fill-brand-byzantine mr-2.5 h-6 w-6" />
              <span className="mr-1 hidden sm:block">Show </span> Trends from
            </p>
            <Select
              filterSelect
              list={list}
              value={selectedTabs?.chartCurrentYear}
              placeholder="Status"
              otherClasses={{
                container: 'w-fit',
                button:
                  ' !bg-transparent !ring-0  !p-0 !text-brand-byzantine !font-bold [&>svg>path]:!fill-current',
                listContainer: '!top-8  !p-2 ',
                list: '!py-1 [&>button]:text-center',
              }}
              onSelect={(e) =>
                setSelectedTabs((prev) => ({
                  ...prev,
                  chartCurrentYear: e,
                }))
              }
              errorMessage=""
            />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-x-15 gap-y-6 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Total Incidents Reported
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of incidents reported vs. month.
            </p>
            <div className="relative mt-6 w-full">
              {data?.detailsInsightsTrends?.totalIncidentReportedChart
                ?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'global-scrollbar-style relative pb-4',
                  data?.detailsInsightsTrends?.totalIncidentReportedChart
                    ?.length === 0 && 'opacity-40',
                  data?.detailsInsightsTrends?.totalIncidentReportedChart
                    ?.length > 5 && 'overflow-auto',
                )}
              >
                <Chart
                  options={totalIncidentsReported?.options as any}
                  series={totalIncidentsReported?.series}
                  type="line"
                  height={200}
                  width={
                    data?.detailsInsightsTrends?.totalIncidentReportedChart
                      ?.length > 5
                      ? `${data?.detailsInsightsTrends?.totalIncidentReportedChart?.length * 100}px`
                      : '100%'
                  }
                />
              </div>
            </div>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Total Incidents Reported by Department
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of incidents reported vs. department.
            </p>
            <div className="relative mt-6 w-full">
              {data?.detailsInsightsTrends
                ?.totalIncidentReportedByDepartmentChart?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'global-scrollbar-style relative pb-4',
                  data?.detailsInsightsTrends
                    ?.totalIncidentReportedByDepartmentChart?.length === 0 &&
                    'opacity-40',
                  data?.detailsInsightsTrends
                    ?.totalIncidentReportedByDepartmentChart?.length > 5 &&
                    'overflow-auto',
                )}
              >
                <Chart
                  options={totalIncidentsReportedByDepartment?.options as any}
                  series={totalIncidentsReportedByDepartment?.series}
                  type="line"
                  height={200}
                  width={
                    data?.detailsInsightsTrends
                      ?.totalIncidentReportedByDepartmentChart?.length > 5
                      ? `${data?.detailsInsightsTrends?.totalIncidentReportedByDepartmentChart?.length * 100}px`
                      : '100%'
                  }
                />
              </div>
            </div>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Corrective Actions Overview
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              {selectedTabs?.correctiveActionsOverview ===
              'By-Avg-Time-of-Closure'
                ? 'This is an overview of all corrective actions by average time of closure.'
                : selectedTabs?.correctiveActionsOverview === 'By-Department'
                  ? 'This is an overview of all corrective actions by department.'
                  : 'This is an overview of all corrective actions which have been completed and closed.'}
            </p>
            <div className="mt-4 flex items-center gap-0">
              {Object.values(
                StatisticsControllerGetStatisticsInsightCorrectiveActionOverviewEnum,
              )?.map((node, index) => (
                <button
                  key={index}
                  onClick={() =>
                    setSelectedTabs((prev) => ({
                      ...prev,
                      correctiveActionsOverview: node,
                    }))
                  }
                  className={clsx(
                    'font-inter-variable global-transition cursor-pointer rounded-md border px-1.5 py-2 text-xs leading-4 capitalize md:px-15 md:text-sm',
                    selectedTabs.correctiveActionsOverview === node
                      ? 'bg-brand-byzantine border-transparent font-semibold text-white'
                      : 'text-neutral-tones-text hover:border-neutral-tones-text/10 hover:text-brand-byzantine border-transparent bg-transparent font-normal',
                  )}
                >
                  {node?.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
            <div
              className={clsx(
                'relative mt-6 w-full',
                selectedTabs?.correctiveActionsOverview !==
                  'By-Avg-Time-of-Closure' && 'main-donut-chart-action',
              )}
            >
              {data?.detailsInsightsTrends?.correctiveActionOverviewChart?.every(
                (value) => !value?.total,
              ) && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <HammerIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              {selectedTabs?.correctiveActionsOverview ===
              'By-Avg-Time-of-Closure' ? (
                <div
                  className={clsx(
                    'global-scrollbar-style relative pb-4',
                    data?.detailsInsightsTrends?.correctiveActionOverviewChart?.every(
                      (value) => !value?.total,
                    ) && 'opacity-40',

                    data?.detailsInsightsTrends?.correctiveActionOverviewChart
                      ?.length > 5 && 'overflow-auto',
                  )}
                >
                  <Chart
                    options={
                      correctiveActionsOverviewByDepartment?.options as any
                    }
                    series={correctiveActionsOverviewByDepartment?.series}
                    type={'line'}
                    height={200}
                    key={
                      selectedTabs?.correctiveActionsOverview +
                      JSON.stringify(
                        data?.detailsInsightsTrends
                          ?.correctiveActionOverviewChart,
                      )
                    }
                    width={
                      data?.detailsInsightsTrends?.correctiveActionOverviewChart
                        ?.length > 5
                        ? `${
                            data?.detailsInsightsTrends
                              ?.correctiveActionOverviewChart?.length * 100
                          }px`
                        : '100%'
                    }
                  />
                </div>
              ) : (
                <div
                  className={clsx(
                    'global-scrollbar-style relative pb-4',
                    data?.detailsInsightsTrends?.correctiveActionOverviewChart?.every(
                      (value) => !value?.total,
                    ) && 'opacity-40',
                  )}
                >
                  <Chart
                    options={correctiveActionsOverview?.options as any}
                    series={correctiveActionsOverview?.series}
                    type={'donut'}
                    height={200}
                    key={
                      selectedTabs?.correctiveActionsOverview +
                      JSON.stringify(
                        data?.detailsInsightsTrends
                          ?.correctiveActionOverviewChart,
                      )
                    }
                    width={'100%'}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <div className="flex items-center justify-between">
              <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
                Incidents by Severity
              </h6>
              {selectedTabs.incidentsBySeverity === 'month' && (
                <Select
                  filterSelect
                  list={Array.from({ length: 12 }, (_, i) => ({
                    label: moment().month(i).format('MMM'),
                    value: (i + 1).toString(),
                  }))}
                  value={selectedTabs?.incidentsBySeverityMonth}
                  placeholder="Month"
                  otherClasses={{
                    container: 'w-fit',
                    button:
                      ' !bg-transparent !ring-0  !p-0 !gap-1 [&>svg>path]:!fill-current',
                    listContainer:
                      '!top-8  !p-2 !w-16 left-2/4 -translate-x-2/4 !z-20',
                    list: '!py-1 [&>button]:text-center',
                  }}
                  onSelect={(e) =>
                    setSelectedTabs((prev) => ({
                      ...prev,
                      incidentsBySeverityMonth: e,
                    }))
                  }
                  errorMessage=""
                />
              )}
            </div>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of incidents reported grouped by severity.
            </p>
            <div className="mt-4 flex items-center gap-0.5 md:gap-2">
              {Object.values(
                StatisticsControllerGetStatisticsInsightSeverityActiveEnum,
              )?.map((node, index) => (
                <button
                  key={index}
                  onClick={() =>
                    setSelectedTabs((prev) => ({
                      ...prev,
                      incidentsBySeverity: node,
                    }))
                  }
                  className={clsx(
                    'font-inter-variable global-transition cursor-pointer rounded-md border px-2 py-2 text-xs leading-4 capitalize md:px-15 md:text-sm',
                    selectedTabs.incidentsBySeverity === node
                      ? 'bg-brand-byzantine border-transparent font-bold text-white'
                      : 'text-neutral-tones-text hover:border-neutral-tones-text/10 hover:text-brand-byzantine border-transparent bg-transparent font-normal',
                  )}
                >
                  By {node}
                </button>
              ))}
            </div>
            <div className="main-donut-chart relative mt-6 w-full">
              {data?.detailsInsightsTrends?.incidentSeverityChart?.every(
                (value) => !value?.total,
              ) && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'relative',
                  data?.detailsInsightsTrends?.incidentSeverityChart?.every(
                    (value) => !value?.total,
                  ) && 'opacity-40',
                )}
              >
                <Chart
                  options={incidentsBySeverityOverview?.options as any}
                  series={incidentsBySeverityOverview?.series}
                  type="donut"
                  height={200}
                  key={JSON.stringify(
                    data?.detailsInsightsTrends?.incidentSeverityChart,
                  )}
                  width="100%"
                />
              </div>
            </div>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <div className="flex items-center justify-between">
              <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
                Incidents by Type
              </h6>
              {selectedTabs.incidentByType === 'month' && (
                <Select
                  filterSelect
                  list={Array.from({ length: 12 }, (_, i) => ({
                    label: moment().month(i).format('MMM'),
                    value: (i + 1).toString(),
                  }))}
                  value={selectedTabs?.incidentsByTypeMonth}
                  placeholder="Month"
                  otherClasses={{
                    container: 'w-fit',
                    button:
                      ' !bg-transparent !ring-0  !p-0 !gap-1 [&>svg>path]:!fill-current',
                    listContainer:
                      '!top-8  !p-2 !w-16 left-2/4 -translate-x-2/4 !z-20',
                    list: '!py-1 [&>button]:text-center',
                  }}
                  onSelect={(e) =>
                    setSelectedTabs((prev) => ({
                      ...prev,
                      incidentsByTypeMonth: e,
                    }))
                  }
                  errorMessage=""
                />
              )}
            </div>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of incidents reported grouped by types.
            </p>
            <div className="mt-4 flex items-center gap-2">
              {Object.values(
                StatisticsControllerGetStatisticsInsightIncidentTypeActiveEnum,
              )?.map((node, index) => (
                <button
                  key={index}
                  onClick={() =>
                    setSelectedTabs((prev) => ({
                      ...prev,
                      incidentByType: node,
                    }))
                  }
                  className={clsx(
                    'font-inter-variable global-transition cursor-pointer rounded-md border px-15 py-2 text-sm leading-4',
                    selectedTabs.incidentByType === node
                      ? 'bg-brand-byzantine border-transparent font-bold text-white'
                      : 'text-neutral-tones-text hover:border-neutral-tones-text/10 hover:text-brand-byzantine border-transparent bg-transparent font-normal capitalize',
                  )}
                >
                  By {node}
                </button>
              ))}
            </div>
            <div className="relative mt-6 w-full">
              {data?.detailsInsightsTrends?.incidentTypeChart?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'global-scrollbar-style relative pb-4',
                  data?.detailsInsightsTrends?.incidentTypeChart?.length ===
                    0 && 'opacity-40',
                  data?.detailsInsightsTrends?.incidentTypeChart?.length > 5 &&
                    'overflow-x-auto',
                )}
              >
                <Chart
                  options={incidentsByType?.options as any}
                  series={incidentsByType?.series}
                  type="line"
                  height={200}
                  width={
                    data?.detailsInsightsTrends?.incidentTypeChart?.length > 5
                      ? `${
                          data?.detailsInsightsTrends?.incidentTypeChart
                            ?.length * 100
                        }px`
                      : '100%'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-0 mt-8 w-full lg:mt-11">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h5 className="text-neutral-tones-text font-inter-variable text-lg font-bold lg:text-xl">
            Injuries Breakdown
          </h5>
        </div>
        <div className="mt-15 grid grid-cols-2 gap-15 md:grid-cols-4 md:gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-10 bg-card-backgrounds/5 col-span-2 border border-black/25 px-15 py-6 lg:px-5">
            <div className="relative flex items-center gap-2.5">
              <InjuriesIcon
                className={clsx(
                  'h-6 min-h-6 w-6 min-w-6 lg:h-10 lg:min-h-10 lg:w-10 lg:min-w-10',
                  data?.tlrr === null
                    ? '[&>path]:fill-brand-posh-pink'
                    : '[&>path]:fill-brand-byzantine',
                )}
              />
              {data?.tlrr === null ? (
                <p className="text-brand-posh-pink gap-1 text-2xl font-bold">
                  Annual employee hours are missing.{' '}
                  <button className="group -tracking-form-label z-20 inline-block cursor-pointer text-sm leading-4 font-bold lg:relative">
                    <InfoIcon className="h-5 w-5 translate-y-1 [&>path]:fill-current" />
                    <p className="font-inter-variable bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden min-w-261 -translate-x-2/4 rounded px-2 py-1 !text-xs font-normal shadow-lg group-hover:block group-active:block">
                      Annual employee hours are missing. Add them in Settings →
                      Employees Annual Working Hours to proceed with the
                      evaluation.
                    </p>
                  </button>
                </p>
              ) : (
                <h6
                  className={clsx(
                    'h1 text-brand-byzantine leading-6 lg:leading-11',
                  )}
                >
                  <CountUp
                    start={0}
                    end={Number(data?.tlrr)}
                    duration={5}
                    preserveValue={true}
                    decimalPlaces={2}
                  />
                </h6>
              )}
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 text-base leading-5 font-medium lg:text-lg">
              Total Recordable Incident Rate (TRIR)
            </p>
          </div>

          <div className="rounded-10 bg-card-backgrounds/5 col-span-2 border border-black/25 px-15 py-6 lg:px-5">
            <div className="flex items-center gap-2.5">
              <RetryIcon className="[&>path]:fill-brand-fair-orange h-6 w-6 lg:h-8 lg:w-8" />

              <h6
                className={clsx(
                  'text-brand-fair-orange font-ulagadi-sans line-clamp-1 text-2xl leading-8 font-bold lg:text-3xl lg:leading-11 2xl:text-4xl',
                )}
              >
                {data?.detailsInsightsTrends?.timeSinceLastInjuryReported}
              </h6>
            </div>
            <p className="text-neutral-tones-text font-inter-variable mt-15 text-base leading-7 font-medium lg:text-lg">
              Time Since Last Injury
            </p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-x-15 gap-y-6 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Type of Loss
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of the types of loss in incidents categorized
              as injury/illness.
            </p>
            <div className="relative mt-6 w-full">
              {data?.detailsInsightsTrends?.typeofLossChart?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'global-scrollbar-style relative pb-4',
                  data?.detailsInsightsTrends?.typeofLossChart?.length === 0 &&
                    'opacity-40',
                  data?.detailsInsightsTrends?.typeofLossChart?.length > 5 &&
                    'overflow-auto',
                )}
              >
                <Chart
                  options={typeOfLoss?.options as any}
                  series={typeOfLoss?.series}
                  type="line"
                  height={200}
                  width={
                    data?.detailsInsightsTrends?.typeofLossChart?.length > 5
                      ? `${
                          data?.detailsInsightsTrends?.typeofLossChart?.length *
                          100
                        }px`
                      : '100%'
                  }
                />
              </div>
            </div>
          </div>

          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <div className="flex items-center justify-between">
              <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
                Body parts affected
              </h6>
            </div>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of the body parts affected in incidents
              categorized as injury/illness.
            </p>

            <div className="main-donut-chart relative mt-6 w-full">
              {data?.detailsInsightsTrends?.affectedBodyPartChart?.every(
                (value) => !value?.total,
              ) && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'relative',
                  data?.detailsInsightsTrends?.affectedBodyPartChart?.every(
                    (value) => !value?.total,
                  ) && 'opacity-40',
                )}
              >
                <Chart
                  options={affectedBodyPartOverview?.options as any}
                  series={affectedBodyPartOverview?.series}
                  type="donut"
                  height={200}
                  key={JSON.stringify(
                    data?.detailsInsightsTrends?.affectedBodyPartChart,
                  )}
                  width="100%"
                />
              </div>
            </div>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <div className="flex items-center justify-between">
              <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
                Injury Classification
              </h6>
            </div>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of the classifications made after the
              investigation of injuries.
            </p>

            <div className="main-donut-chart relative mt-6 w-full">
              {data?.detailsInsightsTrends?.classificationIllnessChart?.every(
                (value) => !value?.total,
              ) && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'relative',
                  data?.detailsInsightsTrends?.classificationIllnessChart?.every(
                    (value) => !value?.total,
                  ) && 'opacity-40',
                )}
              >
                <Chart
                  options={classificationIllnessOverview?.options as any}
                  series={classificationIllnessOverview?.series}
                  type="donut"
                  height={200}
                  key={JSON.stringify(
                    data?.detailsInsightsTrends?.classificationIllnessChart,
                  )}
                  width="100%"
                />
              </div>
            </div>
          </div>

          <div className="rounded-10 bg-card-backgrounds/5 w-full border border-black/25 px-15 py-15 lg:px-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Total Injuries Reported by Department
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of injuries reported vs. department.
            </p>
            <div className="relative mt-6 w-full">
              {data?.detailsInsightsTrends?.totalInjuriesByDepartmentChart
                ?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent backdrop-blur-[2px]">
                  <div className="-mt-6 flex flex-col items-center justify-center gap-2.5">
                    <BlueBulbIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                    <p className="text-neutral-tones-text font-inter-variable text-xl leading-6 font-bold">
                      No data available
                    </p>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  'global-scrollbar-style relative pb-4',
                  data?.detailsInsightsTrends?.totalInjuriesByDepartmentChart
                    ?.length === 0 && 'opacity-40',
                  data?.detailsInsightsTrends?.totalInjuriesByDepartmentChart
                    ?.length > 5 && 'overflow-x-auto',
                )}
              >
                <Chart
                  options={totalInjuriesReportedByDepartment?.options as any}
                  series={totalInjuriesReportedByDepartment?.series}
                  type="line"
                  height={200}
                  width={
                    data?.detailsInsightsTrends?.totalInjuriesByDepartmentChart
                      ?.length > 5
                      ? `${
                          data?.detailsInsightsTrends
                            ?.totalInjuriesByDepartmentChart?.length * 100
                        }px`
                      : '100%'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 w-full lg:mt-11">
        <h5 className="text-neutral-tones-text font-inter-variable text-lg font-bold lg:text-xl">
          Corrective Actions Breakdown
        </h5>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-10 bg-card-backgrounds/5 flex w-full flex-col border border-black/25 p-15 lg:px-6">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Corrective Actions Active
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable min-h-10 text-xs leading-5 font-normal lg:text-base">
              This is an overview of all active corrective actions items.
            </p>
            {stats?.correctiveActionBreakdown?.active?.length > 0 ? (
              <div className="mt-6 mb-15 flex flex-col items-start lg:mt-4">
                {stats?.correctiveActionBreakdown?.active.map((item, index) => (
                  <div
                    key={index}
                    className="border-neutral-tones-text/10 w-full border-b pt-15 pb-15 first:pt-0"
                  >
                    <p className="text-neutral-tones-text/50 font-inter-variable px-2 text-sm font-normal">
                      Due{' '}
                      <span className="text-brand-byzantine">{item.title}</span>
                    </p>
                    <div className="mt-15 flex w-full flex-col gap-1.5">
                      {item.correctiveActions.map((listItem, listIndex) => (
                        <Link
                          to={
                            listItem?.leadAssignee?.user?.id ===
                            userInfo?.user?.id
                              ? `/assigned-to-me/${listItem?.id}`
                              : `/all-corrective-actions/${listItem?.id}`
                          }
                          key={listIndex}
                          className="group hover:bg-neutral-tones-text/5 flex items-center justify-between gap-2 rounded px-2 py-1.5"
                        >
                          <div className="flex flex-1 items-start gap-1">
                            <div className="min-h-5 min-w-5">
                              <ThreeQuarterIcon />
                            </div>
                            <div>
                              <p className="text-neutral-tones-text font-inter-variable group-hover:text-brand-byzantine line-clamp-2 text-sm leading-4 font-normal">
                                {listItem?.title}
                              </p>
                              <span className="font-inter-variable text-neutral-tones-text/50 text-xs font-normal">
                                Assigned to{' '}
                                {listItem?.leadAssignee?.user?.fullName} |{' '}
                                <span
                                  className={clsx(
                                    'font-bold uppercase',
                                    listItem.priority ===
                                      CorrectiveActionsWithOverdueDaysPriorityEnum.Urgent
                                      ? 'text-brand-posh-pink'
                                      : listItem.priority ===
                                          CorrectiveActionsWithOverdueDaysPriorityEnum.High
                                        ? 'text-brand-fair-orange'
                                        : listItem.priority ===
                                            CorrectiveActionsWithOverdueDaysPriorityEnum.Medium
                                          ? 'text-primary-yellow'
                                          : 'text-brand-papery',
                                  )}
                                >
                                  {listItem?.priority}
                                </span>
                              </span>
                            </div>
                          </div>
                          <p
                            className={clsx(
                              'font-inter-variable text-sm leading-4 font-bold uppercase',
                              listItem?.status ===
                                CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum?.Active
                                ? 'text-brand-posh-pink'
                                : listItem?.status ===
                                    CorrectiveActionControllerGetAllCorrectiveActionOrganizationStatusEnum?.Closed
                                  ? 'text-brand-papery'
                                  : 'text-brand-byzantine',
                            )}
                          >
                            {listItem?.status}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex min-h-64 flex-1 flex-col items-center justify-center gap-2">
                <HammerIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                <p className="text-neutral-tones-text font-inter-variable text-center text-xl leading-6 font-bold">
                  No Active Corrective Actions
                </p>
              </div>
            )}
            <Button
              size="smaller"
              href="/all-corrective-actions"
              otherClasses="w-full max-w-136 mt-auto"
            >
              View All
            </Button>
          </div>
          <div className="rounded-10 bg-card-backgrounds/5 flex w-full flex-col border border-black/25 p-15 lg:px-6">
            <h6 className="text-neutral-tones-text font-inter-variable text-base leading-5 font-bold">
              Corrective Actions Overdue
            </h6>
            <p className="text-neutral-tones-text/50 font-inter-variable text-xs leading-5 font-normal lg:text-base">
              This is an overview of all overdue corrective actions items.
            </p>
            {stats?.correctiveActionBreakdown?.overdue.length === 0 ? (
              <div className="flex min-h-64 flex-1 flex-col items-center justify-center gap-2">
                <HammerIcon className="[&>path]:fill-brand-byzantine h-16 w-16" />
                <p className="text-neutral-tones-text font-inter-variable text-center text-xl leading-6 font-bold">
                  No Overdue Corrective Actions
                </p>
              </div>
            ) : (
              <div className="border-neutral-tones-text/10 mt-6 mb-15 flex flex-col items-start gap-1.5 border-b lg:mt-6">
                {stats?.correctiveActionBreakdown?.overdue.map(
                  (item, index) => (
                    <Link
                      to={
                        item?.leadAssignee?.user?.id === userInfo?.user?.id
                          ? `/assigned-to-me/${item?.id}`
                          : `/all-corrective-actions/${item?.id}`
                      }
                      key={index}
                      className="group hover:bg-neutral-tones-text/5 w-full rounded px-2 py-1.5"
                    >
                      <div className="flex flex-1 items-start gap-1">
                        <div className="min-h-5 min-w-5">
                          <ThreeQuarterIcon className="[&>path]:fill-brand-posh-pink group-hover:[&>path]:fill-brand-byzantine" />
                        </div>
                        <div className="flex flex-1 flex-col items-start justify-between gap-1 lg:flex-row lg:items-center">
                          <div>
                            <p className="text-neutral-tones-text font-inter-variable group-hover:text-brand-byzantine line-clamp-2 text-sm leading-4 font-normal capitalize">
                              {item?.title}
                            </p>
                            <div className="mt-1 leading-3">
                              <p className="font-inter-variable text-neutral-tones-text/50 inline text-xs font-normal">
                                Assigned to {item?.leadAssignee?.user?.fullName}{' '}
                                |
                              </p>{' '}
                              <p
                                className={clsx(
                                  'font-inter-variable inline text-xs font-bold uppercase',
                                  item.priority ===
                                    CorrectiveActionsWithOverdueDaysPriorityEnum.Urgent
                                    ? 'text-brand-posh-pink'
                                    : item.priority ===
                                        CorrectiveActionsWithOverdueDaysPriorityEnum.High
                                      ? 'text-brand-fair-orange'
                                      : item.priority ===
                                          CorrectiveActionsWithOverdueDaysPriorityEnum.Medium
                                        ? 'text-primary-yellow'
                                        : 'text-brand-papery',
                                )}
                              >
                                {item?.priority}
                              </p>
                            </div>
                          </div>
                          <p
                            className={clsx(
                              'font-inter-variable text-brand-posh-pink text-sm leading-4 font-bold whitespace-nowrap uppercase',
                            )}
                          >
                            {moment().diff(moment(item?.dueDate), 'days')} DAYS
                            OVERDUE
                          </p>
                        </div>
                      </div>
                    </Link>
                  ),
                )}
              </div>
            )}
            <Button
              size="smaller"
              href="/all-corrective-actions"
              otherClasses="w-full max-w-136 mt-auto"
            >
              View All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
